import { createContext, useContext } from "react";
import { ConfigStore } from "./config.store";
import { AutomationsStore } from "./automation/index.store";
import { IntegrationStore } from "./integration.store";
import { CredentialStore } from "./credential.store";
import { ApplicationStore } from "./application.store";
import { ThemeStore } from "./theme.store";
import { RouterStore } from "./router.store";
import { ModalStore } from "./modal.store";
import { configure } from "mobx";
import { MembersStore } from "./member.store";
import { TeamsStore } from "./teams";
import { SolutionStore } from "./solution.store";
import { ThemeContext } from "styled-components";
import type { ApplicationModel } from "../types/application";
import type { UserPreferences } from "../types/user-preferences";
import type { SolutionModel } from "../types/solution";
import type { InputCandidate } from "../types/candidate";
import type { Workspace } from "../types/workspace";
import { ScheduleStore } from "./schedule.store";
import { LimitsStore } from "./limits.store";
import { JobDetailsStore } from "./job-details.store";
import { JobsStore } from "./jobs.store";
import { ErrorModalStore } from "./error-modal.store";
import { FlowCanvasStore } from "./flow-canvas.store";
import { featureFlagsStore } from "./feature-flags.store";
import type { FeatureFlagsStore } from "./feature-flags.store";

configure({ isolateGlobalState: true });

export class RootStore {
  config: ConfigStore;
  automations: AutomationsStore;
  integrations: IntegrationStore;
  credentials: CredentialStore;
  applications: ApplicationStore;
  theme: ThemeStore;
  router: RouterStore;
  modal: ModalStore;
  errorModal: ErrorModalStore;
  members: MembersStore;
  teams: TeamsStore;
  solutions: SolutionStore;
  inputToFocus: InputCandidate | undefined;
  scheduleStore: ScheduleStore;
  limits: LimitsStore;
  jobsStore: JobsStore;
  jobDetails: JobDetailsStore;
  flowCanvas: FlowCanvasStore;
  featureFlags: FeatureFlagsStore;

  constructor() {
    this.config = new ConfigStore();
    this.automations = new AutomationsStore();
    this.integrations = new IntegrationStore();
    this.credentials = new CredentialStore();
    this.applications = new ApplicationStore();
    this.theme = new ThemeStore();
    this.router = new RouterStore();
    this.modal = new ModalStore();
    this.errorModal = new ErrorModalStore();
    this.members = new MembersStore();
    this.teams = new TeamsStore();
    this.solutions = new SolutionStore();
    this.scheduleStore = new ScheduleStore();
    this.limits = new LimitsStore();
    this.jobDetails = new JobDetailsStore();
    this.jobsStore = new JobsStore();
    this.flowCanvas = new FlowCanvasStore();
    // shared module singleton -- see feature-flags.store.ts
    this.featureFlags = featureFlagsStore;
  }

  init(
    accountId: string,
    solutionId: string,
    engineUrl: string,
    limitsUrl: string,
    accessToken: string,
    smartsuiteBackendUrl: string,
    applications: ApplicationModel[] | undefined,
    applicationId: string | undefined,
    userPreferences: UserPreferences,
    solution: SolutionModel,
    solutions: SolutionModel[] | undefined,
    workspace: Workspace,
    baseUrl: string,
  ): void {
    this.config.init(
      accountId,
      solutionId,
      engineUrl,
      limitsUrl,
      accessToken,
      smartsuiteBackendUrl,
      userPreferences,
      solution,
      workspace,
      baseUrl,
    );

    // clear application store
    this.applications.setList({ applications: [] });
    // checking due to how monolith works
    if (applications !== undefined && applications.length > 0) {
      this.applications.setList({
        applications: applications.map((application) => ({
          id: application.id,
          label: application.name,
          solution: application.solution,
        })),
      });
    }

    this.applications.selectedApplicationId = applicationId;

    if (solutions !== undefined && solutions.length > 0) {
      this.solutions.setList(solutions);
    }

    // Members and teams are cached per solution (see MembersStore), and the
    // builder can be reopened against a different solution or workspace, so the
    // cache must not survive an init.
    this.members.reset();
    this.teams.reset();

    this.automations.purgeList();
    this.router.resetPath();
  }
}
export const rootStore = new RootStore();
export const RootStoreContext = createContext(rootStore);
export const useTheme = (): ThemeStore => useContext(ThemeContext);
